import * as React from "react";
import { Namespace, useT } from "@racoon/i18n";
import { UserRound } from "lucide-react";
import { FieldRow, Input, PasswordInput, SearchableSelect } from "@racoon/ui";
import { ConnectProto } from "@racoon/api";
import { PASSWORD_MIN_LENGTH } from "@racoon/lib";
import { roleKeyToEnum, type RoleKey } from "~/shared/api/user/user.grpc";
import { formatPhone } from "~/shared/lib/phone";
import { type useUserForm, type UserFormOrg } from "../model/use-user-form";
import { GroupsDraftField, GroupsField } from "./groups-field";
import { TeamsDraftField, TeamsField } from "./teams-field";

/** Label column sized to the card's field grid; the control spans the rest. */
const ROW = "!grid-cols-[202px_1fr_1fr]";

interface UserFieldsProps {
  form: ReturnType<typeof useUserForm>;
  org: UserFormOrg;
  user: ConnectProto.User | null;
  showGroups?: boolean;
}

/** The account fields themselves — identical whether creating or editing. */
export function UserFields({ form, org, user, showGroups }: UserFieldsProps) {
  const t = useT();
  const { isEdit, fields, orgOptions, isOrgsLoading, roleOptions, errorMessage, passwordOk } = form;
  const stopPropagation = (e: React.KeyboardEvent) => e.stopPropagation();
  const roleEnum = roleKeyToEnum(fields.role);

  const orgReadonlyName =
    (user &&
      org.mode === "readonly" &&
      (org.orgMap?.get(String(user.ownerOrganizationId)) || user.ownerOrganization?.name)) ||
    "—";

  return (
    <div className="flex gap-4 p-4">
      <div className="flex-1 min-w-0 flex flex-col gap-3">
        {org.mode !== "fixed" && (
          <FieldRow
            className={ROW}
            htmlFor="user-form-org"
            label={t(Namespace.Employees, "table.organization")}
          >
            {org.mode === "readonly" ? (
              <Input
                id="user-form-org"
                value={orgReadonlyName}
                disabled
                className="h-9 bg-[#e8edf3] text-[#94a3b8] cursor-not-allowed opacity-100 border-transparent"
              />
            ) : (
              <SearchableSelect
                id="user-form-org"
                value={fields.pickedOrgId}
                options={orgOptions}
                onChange={fields.setPickedOrgId}
                loading={isOrgsLoading}
                placeholder={t(Namespace.Organizations, "selectOrganization")}
                searchPlaceholder={t(Namespace.Organizations, "searchOrganization")}
                size="md"
              />
            )}
          </FieldRow>
        )}

        <FieldRow
          className={ROW}
          htmlFor="user-form-last-name"
          label={t(Namespace.Employees, "create.lastName.label")}
        >
          <Input
            id="user-form-last-name"
            placeholder={t(Namespace.Employees, "create.lastName.placeholder")}
            value={fields.lastName}
            onChange={(e) => fields.setLastName(e.target.value)}
            onKeyDown={stopPropagation}
            className="h-9"
          />
        </FieldRow>

        <FieldRow
          className={ROW}
          htmlFor="user-form-first-name"
          label={t(Namespace.Employees, "create.firstName.label")}
        >
          <Input
            id="user-form-first-name"
            placeholder={t(Namespace.Employees, "create.firstName.placeholder")}
            value={fields.firstName}
            onChange={(e) => fields.setFirstName(e.target.value)}
            onKeyDown={stopPropagation}
            className="h-9"
          />
        </FieldRow>

        <FieldRow
          className={ROW}
          htmlFor="user-form-role"
          label={t(Namespace.Employees, "create.role.label")}
        >
          <SearchableSelect
            id="user-form-role"
            value={fields.role}
            options={roleOptions}
            onChange={(next) => fields.setRole(next as RoleKey)}
            placeholder={t(Namespace.Employees, "create.role.placeholder")}
            searchPlaceholder={t(Namespace.Employees, "searchRole")}
            size="md"
          />
        </FieldRow>

        <FieldRow
          className={ROW}
          htmlFor="user-form-licence"
          label={t(Namespace.Employees, "create.driverLicense.label")}
        >
          <Input
            id="user-form-licence"
            placeholder={t(Namespace.Employees, "create.driverLicense.placeholder")}
            value={fields.driverLicense}
            onChange={(e) => fields.setDriverLicense(e.target.value)}
            onKeyDown={stopPropagation}
            className="h-9"
          />
        </FieldRow>

        <FieldRow
          className={ROW}
          htmlFor="user-form-email"
          label={t(Namespace.Employees, "create.email.label")}
        >
          <Input
            id="user-form-email"
            type="email"
            placeholder={t(Namespace.Employees, "create.email.placeholder")}
            value={fields.email}
            onChange={(e) => fields.setEmail(e.target.value)}
            onKeyDown={stopPropagation}
            className="h-9"
          />
        </FieldRow>

        <FieldRow
          className={ROW}
          htmlFor="user-form-phone"
          label={t(Namespace.Employees, "create.phone.label")}
        >
          <Input
            id="user-form-phone"
            type="tel"
            placeholder="+7 777 123 45 67"
            value={fields.phone}
            onChange={(e) => fields.setPhone(formatPhone(e.target.value))}
            onKeyDown={stopPropagation}
            className="h-9"
          />
        </FieldRow>

        {/* The set-password RPC is org-gated; hide it for org-less users. */}
        {(!isEdit || !!user?.ownerOrganizationId) && (
          <FieldRow
            className={ROW}
            htmlFor="user-form-password"
            label={
              isEdit
                ? t(Namespace.Employees, "setPassword.label")
                : t(Namespace.Employees, "create.password.label")
            }
          >
            <PasswordInput
              id="user-form-password"
              showLabel={false}
              placeholder={
                isEdit
                  ? t(Namespace.Employees, "setPassword.placeholder")
                  : t(Namespace.Employees, "create.password.placeholder")
              }
              value={fields.password}
              onChange={(e) => fields.setPassword(e.target.value)}
              onKeyDown={stopPropagation}
              className="h-9"
            />
          </FieldRow>
        )}

        {!!fields.password && !passwordOk && (
          <p className="pl-[214px] text-xs text-red-600">
            {t(Namespace.Auth, "passwordMinLength", { min: PASSWORD_MIN_LENGTH })}
          </p>
        )}

        {showGroups && isEdit && user && (
          <>
            <FieldRow className={ROW} label={t(Namespace.Employees, "create.groups.label")}>
              <GroupsField user={user} />
            </FieldRow>
            <FieldRow className={ROW} alignTop label={t(Namespace.Employees, "create.teams.label")}>
              <TeamsField user={user} />
            </FieldRow>
          </>
        )}
        {showGroups && !isEdit && form.createOrgId !== undefined && (
          <>
            <FieldRow className={ROW} label={t(Namespace.Employees, "create.groups.label")}>
              <GroupsDraftField
                ownerOrganizationId={form.createOrgId}
                role={fields.role}
                value={fields.groupIds}
                onChange={fields.setGroupIds}
              />
            </FieldRow>
            {/* Teams are per role: nothing to offer until one is picked. */}
            {roleEnum !== undefined && (
              <FieldRow className={ROW} label={t(Namespace.Employees, "create.teams.label")}>
                <TeamsDraftField
                  ownerOrganizationId={form.createOrgId}
                  role={roleEnum}
                  value={fields.teamIds}
                  onChange={fields.setTeamIds}
                />
              </FieldRow>
            )}
          </>
        )}

        {errorMessage && (
          <div className="ml-[214px] text-sm text-red-600 bg-red-50 border border-red-200 rounded-md p-3">
            {errorMessage}
          </div>
        )}
      </div>

      <div className="w-[320px] shrink-0 flex flex-col">
        <div className="w-[320px] h-[320px] rounded-lg bg-[#f2f4f7] flex items-center justify-center overflow-hidden">
          <UserRound className="w-28 h-28 text-[#b0b7c3]" />
        </div>
        <button
          type="button"
          className="mt-2.5 self-start text-sm text-brand-blue hover:opacity-70 transition-opacity"
        >
          {t(Namespace.Employees, "uploadPhoto")}
        </button>
      </div>
    </div>
  );
}
